"use client";

import { useDict } from "@/components/i18n/DictProvider";
import Image from "next/image";
import { Headphones } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { RichHtml } from "@/components/ui/RichHtml";
import { AudioPlayer } from "@/components/player/AudioPlayer";
import { PodcastPlayer } from "@/components/player/PodcastPlayer";
import { PaywallCard } from "./PaywallCard";
import { formatDuration, formatPersonName } from "@/lib/format";
import { langPath, type Lang } from "@/lib/i18n";
import { modulePoster } from "@/lib/lecturehub/poster";
import type { ModuleDetail } from "@/lib/lecturehub/types";
import { OrganizationChips } from "./OrganizationChips";

export function PodcastView({
  module: m,
  lang,
  isAuthed,
  isSubscription,
  cfHash = null,
}: {
  module: ModuleDetail;
  lang: Lang;
  isAuthed: boolean;
  isSubscription: boolean;
  /** Portal.cloudflareAccountHash — CF-optimized cover instead of raw uri. */
  cfHash?: string | null;
}) {
  const t = useDict();
  const podcast = m.podcast;
  const isSeries =
    podcast?.audioType === "simple_series" || podcast?.audioType === "section_series";
  const cover = modulePoster(m, cfHash);
  const hasAbout = !!(m.abstract || m.description);
  const instructors = podcast?.instructors ?? [];
  const totalDuration = isSeries
    ? (podcast?.audios ?? []).reduce((s, a) => s + (a.audio?.duration ?? 0), 0)
    : (podcast?.audio?.duration ?? 0);

  return (
    <article className="grid gap-6 min-w-0">
      {hasAbout || !isSeries ? (
        <section className="course-section rounded-card p-6 md:p-8 bg-card border border-border/60">
          {/* No section title — the cover image + player make the card
              self-explanatory ("about podcast" would be redundant chrome).
              Image left + text right at sm+ (stacks on mobile). Player drops
              below as a full-width row inside the same card. */}
          <div className="grid sm:grid-cols-[180px_1fr] gap-5 sm:gap-6 items-start">
            <div>
              <div className="relative w-full aspect-square rounded-card overflow-hidden bg-muted border border-border/60">
                {cover ? (
                  <Image
                    src={cover}
                    alt={m.name ?? ""}
                    fill
                    sizes="(min-width: 640px) 180px, 100vw"
                    className="object-cover"
                  />
                ) : (
                  <span className="absolute inset-0 grid place-items-center text-foreground/40 font-display font-extrabold uppercase tracking-[0.22em] text-[12px]">
                    {t.detail.typePodcast}
                  </span>
                )}
              </div>

              {/* Context below the cover: duration + the people on the
                  podcast. Apple-Podcasts editorial pattern — instead of
                  letting the cover image stand alone, show what's inside
                  and who's behind it. */}
              {totalDuration > 0 ? (
                <p className="mt-3 flex items-center gap-1.5 text-[11px] font-display font-extrabold uppercase tracking-[0.22em] text-foreground/55">
                  <Headphones className="size-3 text-accent" aria-hidden />
                  {formatDuration(totalDuration)}
                </p>
              ) : null}

              {instructors.length > 0 ? (
                <div className="mt-4">
                  <p className="mb-2 text-[10.5px] font-display font-extrabold uppercase tracking-[0.22em] text-foreground/45">
                    {instructors.length === 1 ? t.detail.featuringOne : t.detail.featuringLabel}
                  </p>
                  <ul className="grid gap-2.5">
                    {instructors.slice(0, 4).map((ins) => {
                      const fullName = formatPersonName(ins);
                      const portrait = ins.thumb?.uri ?? ins.image?.uri;
                      return (
                        <li key={ins.id} className="flex items-center gap-2.5 min-w-0">
                          <span className="relative size-7 rounded-full overflow-hidden bg-muted shrink-0 ring-1 ring-inset ring-border/60">
                            {portrait ? (
                              <Image
                                src={portrait}
                                alt={fullName}
                                fill
                                sizes="28px"
                                className="object-cover"
                              />
                            ) : (
                              <span className="absolute inset-0 grid place-items-center text-[8px] font-display font-bold uppercase tracking-[0.16em] text-foreground/40">
                                {(ins.name?.[0] ?? "") + (ins.surname?.[0] ?? "")}
                              </span>
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-display font-semibold text-[12.5px] text-foreground leading-tight truncate">
                              {fullName || "—"}
                            </span>
                            {ins.title ? (
                              <span className="block text-[10.5px] text-foreground/55 leading-tight truncate">
                                {ins.title}
                              </span>
                            ) : null}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>
            <div className="min-w-0">
              {m.abstract ? (
                <RichHtml
                  html={m.abstract}
                  className="relative pl-5 border-l-2 border-accent/70 text-[15px] md:text-[17px] leading-relaxed text-foreground/85 mb-4"
                />
              ) : null}
              {m.description ? <RichHtml html={m.description} /> : null}
              {!isSeries ? (
                <div
                  className="mt-6 rounded-card p-4 md:p-5"
                  style={{
                    background:
                      "linear-gradient(180deg, color-mix(in srgb, var(--color-accent) 7%, transparent) 0%, color-mix(in srgb, var(--color-accent) 3%, transparent) 100%)",
                    boxShadow:
                      "inset 0 0 0 1px color-mix(in srgb, var(--color-accent) 30%, transparent), 0 1px 2px rgb(0 0 0 / 0.04)",
                  }}
                >
                  <p className="flex items-center gap-2 mb-3 text-[11px] font-display font-extrabold uppercase tracking-[0.22em] text-accent">
                    <Headphones className="size-3.5" aria-hidden />
                    {t.detail.listenHeading}
                  </p>
                  {podcast?.audio?.uri ? (
                    <PodcastPlayer src={podcast.audio.uri} title={m.name ?? "Audio"} />
                  ) : (
                    <PaywallCard
                      lang={lang}
                      aspect="auto"
                      className="!aspect-auto py-8"
                      hideCta
                      isAuthed={isAuthed}
                      accessLevel={m.accessLevel}
                      price={m.price}
                      checkoutHref={langPath(lang, `/checkout/${m.id}`)}
                      isSubscription={isSubscription}
                    />
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {isSeries && podcast?.audios?.length ? (
        <SectionCard title={t.detail.episodes}>
          <ul className="grid gap-4">
            {podcast.audios.map((ep) => (
              <li key={ep.id} className="rounded-card border border-border/60 p-5">
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <h3 className="font-display font-semibold text-[15px] flex items-center gap-2 text-foreground">
                    {!ep.audio?.uri && !ep.free ? (
                      <span aria-label="locked" className="text-muted-foreground">
                        🔒
                      </span>
                    ) : null}
                    {ep.title ?? "Episode"}
                  </h3>
                  {ep.free ? <Badge tone="success">{t.detail.free}</Badge> : null}
                </div>
                {ep.abstract ? (
                  <p className="text-muted-foreground text-[13px] mb-3">{ep.abstract}</p>
                ) : null}
                {ep.audio?.uri ? (
                  <AudioPlayer src={ep.audio.uri} title={ep.title ?? ""} compact />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t.detail.subscribe} — {formatDuration(ep.audio?.duration)}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : null}

      <OrganizationChips organizations={m.organizations} t={t} lang={lang} />
    </article>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="course-section rounded-card p-6 md:p-8 bg-card border border-border/60">
      <h2 className="!text-[20px] md:!text-[22px] !font-semibold !leading-tight !text-foreground !mb-5">
        {title}
      </h2>
      {children}
    </section>
  );
}
